/**
 * OutboxWorkerBoot (R7.2).
 *
 * Invisible component mounted inside the authenticated AppShell.
 * Triggers the outbox worker:
 *   1. On first mount (app start / session restore).
 *   2. Every time the network transitions offline → online.
 *
 * Must be rendered inside both <OutboxProvider> and <AuthContext>.
 */

import { useEffect, useRef } from 'react';
import { useOutbox } from './outboxStore';
import { runOutboxWorker } from './outboxWorker';
import { addNetworkListener, getIsOnline } from '../platform/network';
import { useAuth } from '../context/AuthContext';

export function OutboxWorkerBoot() {
  const { user } = useAuth();
  const { items, isProcessing, setStatus, removeItem, setProcessing } = useOutbox();

  // Stable ref to the latest items/isProcessing (avoid re-subscribing to network listener)
  const itemsRef = useRef(items);
  const isProcessingRef = useRef(isProcessing);
  itemsRef.current = items;
  isProcessingRef.current = isProcessing;

  const stableSetStatus = useRef(setStatus);
  stableSetStatus.current = setStatus;
  const stableRemove = useRef(removeItem);
  stableRemove.current = removeItem;
  const stableSetProcessing = useRef(setProcessing);
  stableSetProcessing.current = setProcessing;

  const triggerSync = useRef(() => {
    runOutboxWorker({
      getItems: () => itemsRef.current,
      isProcessing: () => isProcessingRef.current,
      setProcessing: (v) => stableSetProcessing.current(v),
      setStatus: (id, status, error) => stableSetStatus.current(id, status, error),
      removeItem: (id) => stableRemove.current(id),
    });
  });

  // Boot sync on mount (if authenticated)
  useEffect(() => {
    if (!user) return;
    getIsOnline().then((online) => {
      if (online) triggerSync.current();
    });
  }, [user]);

  // Auto-sync when network comes back online
  useEffect(() => {
    if (!user) return;
    const unsubscribe = addNetworkListener((online) => {
      if (online) triggerSync.current();
    });
    return unsubscribe;
  }, [user]);

  return null;
}
